import { atom, useAtom, type PrimitiveAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { useCallback } from 'react';
import { OwnershipGraphResponse } from 'types/api-responses';
import { ProgressState } from 'components/title-chain-validator/types';

type ValidationStatus =
  'pending' | 'processing' | 'progress' | 'completed' | 'error';

interface TitleValidationJobResponse {
  validation_id: string;
  status: ValidationStatus;
  channel: string;
  total_steps: number;
}

interface ValidationNotificationPayload {
  validation_id: string;
  status: ValidationStatus;
  error_message?: string;
  current_step?: number;
  total_steps?: number;
  step_label?: string;
}

interface DocumentAtoms {
  loadingAtom: PrimitiveAtom<boolean>;
  ownershipGraphAtom: PrimitiveAtom<OwnershipGraphResponse | null>;
  errorAtom: PrimitiveAtom<string | null>;
  progressAtom: PrimitiveAtom<ProgressState | null>;
  timestampAtom: PrimitiveAtom<string | null>;
  validationIdAtom: PrimitiveAtom<string | null>;
}

// Module-level cache of atoms per document ID to persist across component mounts
// Limited to 32 most recently used documents to prevent browser memory issues
const MAX_CACHE_SIZE = 32;
const atomCache = new Map<string, DocumentAtoms>();

function getAtomsForDocument(documentId: string): DocumentAtoms {
  if (atomCache.has(documentId)) {
    const atoms = atomCache.get(documentId)!;
    atomCache.delete(documentId);
    atomCache.set(documentId, atoms);
    return atoms;
  }
  if (atomCache.size >= MAX_CACHE_SIZE) {
    const firstKey = atomCache.keys().next().value;
    if (firstKey !== undefined) {
      atomCache.delete(firstKey);
    }
  }

  const newAtoms: DocumentAtoms = {
    loadingAtom: atom(false),
    ownershipGraphAtom: atom<OwnershipGraphResponse | null>(null),
    errorAtom: atom<string | null>(null),
    progressAtom: atom<ProgressState | null>(null),
    timestampAtom: atom<string | null>(null),
    validationIdAtom: atom<string | null>(null),
  };

  atomCache.set(documentId, newAtoms);
  return newAtoms;
}

export const useTitleValidation = (
  validationUrl: string,
  documentId: string | number
) => {
  const docIdString = documentId.toString();
  const atoms = getAtomsForDocument(docIdString);

  const [loading, setLoading] = useAtom(atoms.loadingAtom);
  const [ownershipGraph, setOwnershipGraph] = useAtom(atoms.ownershipGraphAtom);
  const [error, setError] = useAtom(atoms.errorAtom);
  const [progress, setProgress] = useAtom(atoms.progressAtom);
  const [timestamp, setTimestamp] = useAtom(atoms.timestampAtom);
  const [validationId, setValidationId] = useAtom(atoms.validationIdAtom);

  const triggerValidation = useCallback(
    async (data: {
      target_entity_id: string;
      cutoff_date?: string;
      coverage_threshold: number;
      establishment_ids?: number[];
    }) => {
      setLoading(true);
      setOwnershipGraph(null);
      setError(null);
      setProgress(null);

      try {
        const response = await request.post<
          TitleValidationJobResponse,
          { error?: string }
        >(validationUrl, data, new AbortController());

        if (response.type === 'success') {
          const jobData = response.data;
          const jobValidationId = jobData.validation_id;
          const channel = jobData.channel;
          const totalSteps = jobData.total_steps;
          const eventName = 'validation_result';

          // Store validation_id so we can use it for reconciler
          setValidationId(jobValidationId);

          subscribeToValidationChannel(
            channel,
            eventName,
            jobValidationId,
            docIdString,
            totalSteps,
            (ownershipGraphResult) => {
              setOwnershipGraph(ownershipGraphResult ?? null);
              setLoading(false);
              setProgress(null);
            },
            (errorMessage) => {
              setError(errorMessage);
              setLoading(false);
              setProgress(null);
            },
            (progressState) => {
              setProgress(progressState);
            }
          );
        } else {
          const errorMessage =
            response.type === 'error'
              ? (response.data?.error ?? 'Failed to start validation')
              : response.message;
          setError(errorMessage);
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setProgress(null);
      }
    },
    [
      validationUrl,
      docIdString,
      setLoading,
      setOwnershipGraph,
      setError,
      setProgress,
      setValidationId,
    ]
  );

  return {
    loading,
    ownershipGraph,
    error,
    progress,
    timestamp,
    validationId,
    setTimestamp,
    triggerValidation,
  };
};

function subscribeToValidationChannel(
  channel: string,
  eventName: string,
  validationId: string,
  documentId: string,
  totalSteps: number,
  onComplete: (ownershipGraph: OwnershipGraphResponse | undefined) => void,
  onError: (errorMessage: string) => void,
  onProgress: (progress: ProgressState) => void
) {
  const subscription = subscribe(channel);

  subscription.bind(
    eventName,
    async (payload: ValidationNotificationPayload) => {
      if (payload.status === 'completed') {
        subscription.unsubscribe();

        const resultUrl = `/documents/${documentId}/title_validators/${validationId}`;
        try {
          const response = await request.get<{
            version: 'v2';
            status: string;
            ownershipGraph?: OwnershipGraphResponse;
            ownershipGraphError?: { class: string; message: string };
          }>(resultUrl, new AbortController());

          if ('error' in response) {
            onError(response.error || 'Failed to fetch validation result');
          } else if (response.ownershipGraphError && !response.ownershipGraph) {
            onError(response.ownershipGraphError.message);
          } else {
            onComplete(response.ownershipGraph);
          }
        } catch (err) {
          onError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch validation result'
          );
        }
      } else if (payload.status === 'error') {
        subscription.unsubscribe();
        onError(payload.error_message || 'Validation failed');
      } else if (payload.status === 'progress') {
        onProgress({
          current_step: payload.current_step || 0,
          total_steps: payload.total_steps || totalSteps,
          step_label: payload.step_label || 'Processing...',
        });
      }
    }
  );
}
